import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Wallet } from 'lucide-react';
import { useThemeStore } from '../store/theme';

interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCard: () => void;
  onSelectPayPal: () => void;
  onSelectPaddle?: () => void;
  title?: string;
  description?: string;
  showPaddle?: boolean;
}

export default function PaymentMethodModal({
  isOpen,
  onClose,
  onSelectCard,
  onSelectPayPal,
  onSelectPaddle,
  title = 'Choose Payment Method',
  description = 'Select how you would like to pay',
  showPaddle = false,
}: PaymentMethodModalProps) {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              className={`relative w-full max-w-md rounded-2xl p-6 ${
                isDark ? 'bg-dark-800 border border-dark-600' : 'bg-white shadow-xl'
              }`}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
                  isDark
                    ? 'hover:bg-dark-700 text-text-muted hover:text-white'
                    : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="text-center mb-6">
                <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {title}
                </h2>
                <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  {description}
                </p>
              </div>

              {/* Payment Options */}
              <div className="space-y-3">
                {/* Pay with Card */}
                <button
                  onClick={onSelectCard}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all ${
                    isDark
                      ? 'bg-dark-700 border-dark-600 hover:border-accent-purple hover:bg-dark-600'
                      : 'bg-slate-50 border-slate-200 hover:border-purple-400 hover:bg-purple-50'
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isDark ? 'bg-accent-purple/20' : 'bg-purple-100'
                    }`}
                  >
                    <CreditCard className="w-6 h-6 text-accent-purple" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Pay with Card
                    </p>
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                      Credit or debit card
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <img
                      src="https://cdn.jsdelivr.net/gh/lipis/flag-icons@main/svg/visa.svg"
                      alt="Visa"
                      className="h-5 w-auto"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <img
                      src="https://cdn.jsdelivr.net/gh/lipis/flag-icons@main/svg/mastercard.svg"
                      alt="Mastercard"
                      className="h-5 w-auto"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                </button>

                {/* Pay with PayPal */}
                <button
                  onClick={onSelectPayPal}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all ${
                    isDark
                      ? 'bg-dark-700 border-dark-600 hover:border-[#003087] hover:bg-dark-600'
                      : 'bg-slate-50 border-slate-200 hover:border-[#003087] hover:bg-blue-50'
                  }`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#003087]/10 flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-[#003087]" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      Pay with PayPal
                    </p>
                    <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                      PayPal account or balance
                    </p>
                  </div>
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#003087">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-7.356 6.082h-2.19a2.227 2.227 0 0 0-2.198 1.887l-1.04 6.583-.298 1.89a.641.641 0 0 0 .633.74h3.39c.457 0 .85-.334.922-.788l.038-.195.73-4.626.047-.255a.928.928 0 0 1 .917-.787h.578c3.742 0 6.673-1.52 7.53-5.919.316-1.624.191-2.979-.607-3.985-.215-.272-.48-.508-.78-.715z" />
                  </svg>
                </button>

                {/* Pay with Paddle */}
                {showPaddle && onSelectPaddle && (
                  <button
                    onClick={onSelectPaddle}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all ${
                      isDark
                        ? 'bg-dark-700 border-dark-600 hover:border-[#3D52D5] hover:bg-dark-600'
                        : 'bg-slate-50 border-slate-200 hover:border-[#3D52D5] hover:bg-indigo-50'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#3D52D5]/10 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#3D52D5">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                      </svg>
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Pay with Paddle
                      </p>
                      <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                        Global payments with tax handling
                      </p>
                    </div>
                    <span className="text-[#3D52D5] font-bold text-lg">PD</span>
                  </button>
                )}
              </div>

              {/* Security Badge */}
              <div className="mt-6 flex items-center justify-center gap-2">
                <svg
                  className={`w-4 h-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <span className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                  Secure payment processing by PayPal
                </span>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
